import { useParams, Link, Navigate } from 'react-router-dom';
import {
  getEquipmentItem,
  relatedEquipment,
  alternatesForEquipment,
  equipmentBase,
} from '../lib/equipment';
import { getTechniqueName } from '../lib/exercises';
import { SITE_URL, getStrings, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CtaBlock } from '../components/CtaBlock';
import { Thumb } from '../components/Thumb';

function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${className}`}>
      {children}
    </span>
  );
}

export default function EquipmentPage({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { slug } = useParams();
  const c = getStrings(lang).equipmentChrome;
  const base = equipmentBase(lang);
  const item = slug ? getEquipmentItem(lang, slug) : undefined;
  if (!item) return <Navigate to={base} replace />;

  const url = `${SITE_URL}${base}/${item.slug}`;
  const title = `${item.t.name} — ${c.titleSuffix} | ArmProgress`;
  const description = item.seo.metaDescription || item.t.shortDescription;
  const related = relatedEquipment(item, lang);
  const techniques = item.relatedTechniqueIds.map((id) => ({ id, name: getTechniqueName(id, lang) }));

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.t.name,
    description: item.seo.seoIntro || item.t.shortDescription,
    author: { '@type': 'Organization', name: 'ArmProgress' },
    publisher: { '@type': 'Organization', name: 'ArmProgress' },
  };
  const faq = item.seo.faq || [];
  const faqLd = faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={title}
        description={description}
        canonical={url}
        type="article"
        lang={lang}
        jsonLd={faqLd ? [articleLd, faqLd] : articleLd}
        alternates={alternatesForEquipment(item.id)}
      />
      <BlogHeader lang={lang} />

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to={base} className="text-faint hover:text-ink text-sm">
          {c.backToIndex}
        </Link>

        {/* Hero */}
        <div className="mt-4 mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className="border-line bg-surface/60 text-muted">{c.category[item.category]}</Badge>
            {item.homeFriendly && (
              <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30">{c.homeOnlyLabel}</Badge>
            )}
            {item.skillLevel.map((s) => (
              <Badge key={s} className="border-primary/30 bg-primary/10 text-primary">
                {c.skillLevel[s]}
              </Badge>
            ))}
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-ink leading-tight mb-3">
            {item.t.name}
          </h1>
          {item.seo.seoIntro && <p className="text-muted text-lg leading-relaxed">{item.seo.seoIntro}</p>}
        </div>

        <Thumb
          src={item.imageUrl}
          alt={item.t.name}
          emoji={item.emoji}
          contain
          className="mb-10 aspect-video w-full rounded-2xl border border-line"
        />

        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div className="rounded-2xl border border-line bg-surface/60 p-5">
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-2">{c.whatItIsLabel}</h2>
            <p className="text-muted leading-relaxed">{item.t.whatItIs}</p>
          </div>
          <div className="rounded-2xl border border-line bg-surface/60 p-5">
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-2">{c.whoItsForLabel}</h2>
            <p className="text-muted leading-relaxed">{item.t.whoItsFor}</p>
          </div>
        </div>

        {item.t.howToChoose.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.howToChooseLabel}</h2>
            <ul className="space-y-2">
              {item.t.howToChoose.map((tip, i) => (
                <li key={i} className="flex gap-3 text-muted leading-relaxed">
                  <span className="text-primary flex-shrink-0">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </section>
        )}

        {item.t.howToUse.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.howToUseLabel}</h2>
            <ol className="space-y-3">
              {item.t.howToUse.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/15 border border-primary/30 text-primary font-bold text-sm flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-muted leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {item.t.proTips.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.proTipsLabel}</h2>
            <ul className="space-y-2">
              {item.t.proTips.map((tip, i) => (
                <li key={i} className="flex gap-3 text-muted leading-relaxed">
                  <span className="text-primary flex-shrink-0">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </section>
        )}

        {item.seo.buyingMistakes && item.seo.buyingMistakes.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.mistakesLabel}</h2>
            <ul className="space-y-2">
              {item.seo.buyingMistakes.map((m, i) => (
                <li key={i} className="flex gap-3 text-muted leading-relaxed">
                  <span className="text-red-600 flex-shrink-0">✕</span>
                  {m}
                </li>
              ))}
            </ul>
          </section>
        )}

        {techniques.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-ink mb-3">{c.relatedTechniquesLabel}</h2>
            <div className="flex flex-wrap gap-2">
              {techniques.map((t) => (
                <span
                  key={t.id}
                  className="text-sm bg-elevated text-muted px-3 py-1.5 rounded-full border border-line"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {faq.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.faqLabel}</h2>
            <div className="space-y-5">
              {faq.map((f, i) => (
                <div key={i}>
                  <h3 className="text-ink font-semibold mb-1">{f.q}</h3>
                  <p className="text-muted">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <CtaBlock lang={lang} />

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-ink mb-4">{c.relatedEquipmentLabel}</h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`${base}/${r.slug}`}
                    className="flex items-center gap-2 text-primary hover:text-gold-light rounded-lg px-3 py-2 hover:bg-surface/60 transition"
                  >
                    {r.t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>

      <Footer lang={lang} />
    </div>
  );
}
